import { EuiConfirmModal } from "@inscopix/ideas-eui";
import { useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { queryKeys } from "django/queryKeys";
import { Tool } from "graphql/_Types";
import { getEnvVar } from "ideas.env";
import { useState } from "react";
import { addUtilityToastFailure } from "utils/addUtilityToastFailure";
import { addUtilityToastSuccess } from "utils/addUtilityToastSuccess";
import { getRequestHeaders } from "utils/getRequestHeaders";

/**
 * Deletes a new tool.
 * @param toolVersionId
 */
const deleteToolVersion = async (toolVersionId: number) => {
  const baseUrl = getEnvVar("URL_TRS");
  const url = `${baseUrl}tool/version/${toolVersionId}/schedule_delete/`;
  const headers = await getRequestHeaders();
  const body = { date_deleted: new Date().toISOString() };
  await axios.patch(url, body, { headers });
};

interface ModalDeleteToolVersionProps {
  toolId: Tool["id"];
  toolVersionId: number;
  toolVersionVersion: string;
  onClose: () => void;
}

export const ModalDeleteToolVersion = ({
  toolId,
  toolVersionId,
  toolVersionVersion,
  onClose,
}: ModalDeleteToolVersionProps) => {
  const [loading, setLoading] = useState(false);
  const client = useQueryClient();

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await deleteToolVersion(toolVersionId);
      await client.invalidateQueries({
        queryKey: queryKeys.tool(toolId),
        exact: true,
      });

      addUtilityToastSuccess("Tool version deprecated");
      onClose();
    } catch (error) {
      addUtilityToastFailure("Failed to deprecate tool version");
    }
    setLoading(false);
  };

  return (
    <EuiConfirmModal
      isLoading={loading}
      title={`Permanently deprecate tool version ${toolVersionVersion}?`}
      onCancel={onClose}
      onConfirm={() => void handleConfirm()}
      cancelButtonText="Cancel"
      confirmButtonText="Deprecate Tool Version"
      buttonColor="danger"
      defaultFocusedButton="confirm"
    >
      <p>
        This will make this version of the tool unavailable for all members of
        your organization. Proceed with caution - this cannot be undone.
      </p>
    </EuiConfirmModal>
  );
};
